import React, { useState, useEffect, FC, useCallback, useRef } from 'react';

import PropTypes, { Validator } from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import { PRODUCT_IDS, useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { BasicButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import { OpenIcon, QuestionCircleFilledIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { MenuList, MenuItem } from '@adsk/alloy-react-menu';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';

import AboutBoxModal from './AboutBoxModal';
import { ANALYTICS_TYPES } from '../helpers/analytics';
import isURLValid from '../helpers/isURLValid';
import loadSupportScripts from '../helpers/loadSupportScripts';
import { licenseDataShape } from '../helpers/propTypes';
import { Collection, LicenseDataType } from '../helpers/types';

declare global {
  interface Window {
    solvvyApi: {
      open: () => void;
      clearSolvvyState: () => void;
    };
    zoomCampaignSdk: {
      show: () => void;
      hide: () => void;
      close: () => void;
      open: () => void;
    };
    zoomCampaignSdkConfig: {
      enableSPAMode: boolean;
    };
  }
}

const defaultHelpLink = 'https://help.autodesk.com/view/BUILD/ENU/';

type HelpLinksType = {
  help?: string;
  about?: string;
};

type QuestionMarkMenuProps = {
  initialOpen?: boolean;
  helpLinks?: HelpLinksType;
  currentProductId?: string;
  licenseData?: LicenseDataType | Collection[];
  isProductRequired?: boolean;
  hideContactSupport?: boolean;
};

type UseFeatureFlagOnceReturnValue = {
  value: boolean;
  loading: boolean;
};

const useAllyFeatureFlagOnce = (
  flagName: string,
  defaultValue: boolean,
): UseFeatureFlagOnceReturnValue => {
  const [didLoadOnce, setDidLoadOnce] = useState<boolean>(false);

  const { value, loading } = useAlloyFeatureFlag(flagName, defaultValue);

  useEffect(() => {
    if (!didLoadOnce && !loading) {
      setDidLoadOnce(true);
    }
  }, [loading, didLoadOnce]);

  return {
    value,
    loading: !didLoadOnce,
  };
};

const QuestionMarkMenu: FC<QuestionMarkMenuProps> = ({
  initialOpen,
  helpLinks,
  licenseData,
  currentProductId,
  isProductRequired,
  hideContactSupport,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const [menuOpen, setMenuOpen] = useState(!!initialOpen);
  const [aboutBoxOpen, setAboutBoxOpen] = useState(false);
  const [solvvyLoaded, setSolvvyLoaded] = useState(
    window.SUPPORT_SCRIPTS_LOADED,
  );
  const { value: isZvaChatEnabled, loading } = useAllyFeatureFlagOnce(
    'customer-support-chat-zva',
    false,
  );
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const toggleAboutBox = () => {
    setAboutBoxOpen(true);
    setMenuOpen(false);
    analytics.track(
      ANALYTICS_TYPES.TOPNAVIGATION_HELP_LICENSEATTRIBUTIONS_CLICK,
    );
  };

  const toggleSupport = () => {
    window.solvvyApi && window.solvvyApi.open();

    if (window?.zoomCampaignSdk) {
      window.zoomCampaignSdk.show();
      window.zoomCampaignSdk.open();
    }

    setMenuOpen(false);
    analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_CONTACTSUPPORT_CLICK);
  };

  useEffect(() => {
    if (!loading && (currentProductId || !isProductRequired)) {
      loadSupportScripts(
        () => {
          if (mountedRef.current) {
            setSolvvyLoaded(true);
          }
        },
        currentProductId,
        isZvaChatEnabled,
      );
    }
  }, [currentProductId, isProductRequired, isZvaChatEnabled, loading]);

  const closeChatWindow = useCallback(() => {
    setSolvvyLoaded(true);
    window.zoomCampaignSdk?.close();
    window.zoomCampaignSdk?.hide();
  }, []);

  useEffect(() => {
    window.addEventListener('zoomCampaignSdk:ready', closeChatWindow);

    return () => {
      window.removeEventListener('zoomCampaignSdk:ready', closeChatWindow);
    };
  }, [closeChatWindow]);

  const helpLink =
    helpLinks?.help ||
    (currentProductId &&
      isURLValid(T.translate(`${currentProductId.toUpperCase()}_HELP_LINK`))) ||
    defaultHelpLink;

  return (
    <>
      <AboutBoxModal
        show={aboutBoxOpen}
        onClose={() => setAboutBoxOpen(false)}
        licenseData={licenseData}
      />
      <Flyout
        offset={[0, 20]}
        show={menuOpen}
        onHide={() => setMenuOpen(false)}
        onShow={() => {
          setMenuOpen(true);
        }}
        placement={Flyout.PLACEMENTS.BOTTOM}
        style={{ padding: 0 }}
        content={
          <MenuList style={{ width: 300 }}>
            <MenuItem
              as="a"
              href={helpLink}
              target="_blank"
              rel="noreferrer noopener"
              nested={false}
              showSelection={false}
              onClick={() => {
                setMenuOpen(false);
                analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_CLICK, {
                  source_page: window.location.href,
                });
              }}
              style={{
                justifyContent: 'space-between',
                padding: '10px 16px',
              }}
            >
              <span>{T.translate('HELP')}</span>
              <OpenIcon size={16} style={{ marginLeft: 16 }} />
            </MenuItem>
            <MenuItem
              as="a"
              href="https://learnacc.autodesk.com/"
              target="_blank"
              rel="noreferrer noopener"
              nested={false}
              showSelection={false}
              onClick={() => {
                setMenuOpen(false);
                analytics.track(
                  ANALYTICS_TYPES.TOPNAVIGATION_HELP_LEARNINGCENTER_CLICK,
                );
              }}
              style={{
                justifyContent: 'space-between',
                padding: '10px 16px',
              }}
            >
              <span>{T.translate('LEARNING_CENTER')}</span>
              <OpenIcon size={16} style={{ marginLeft: 16 }} />
            </MenuItem>
            {!hideContactSupport && (
              <MenuItem
                nested={false}
                disabled={!solvvyLoaded}
                showSelection={false}
                onClick={toggleSupport}
                style={{
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                }}
              >
                <span>{T.translate('CONTACT_SUPPORT')}</span>
                {!solvvyLoaded && (
                  <ProgressRing size={ProgressRing.SIZES.XSMALL} />
                )}
              </MenuItem>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <MenuItem
              as="a"
              href="https://www.autodesk.com/company/terms-of-use/en/general-terms"
              target="_blank"
              rel="noreferrer noopener"
              nested={false}
              showSelection={false}
              onClick={() => {
                setMenuOpen(false);
                analytics.track(
                  ANALYTICS_TYPES.TOPNAVIGATION_HELP_TERMSCONDITIONS_CLICK,
                );
              }}
              style={{
                justifyContent: 'space-between',
                padding: '10px 16px',
              }}
            >
              <span>{T.translate('TERMS_AND_CONDITIONS')}</span>
              <OpenIcon size={16} />
            </MenuItem>
            {!helpLinks?.about && (
              <MenuItem
                nested={false}
                showSelection={false}
                onClick={toggleAboutBox}
                style={{
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                }}
              >
                <span>{T.translate('LICENSE_ATTRIBUTIONS')}</span>
              </MenuItem>
            )}
            {helpLinks?.about && (
              <MenuItem
                as="a"
                href={helpLinks?.about}
                target="_blank"
                rel="noreferrer noopener"
                nested={false}
                showSelection={false}
                onClick={() => setMenuOpen(false)}
                style={{
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                }}
              >
                <span>{T.translate('ABOUT')}</span>
                <OpenIcon size={16} style={{ marginLeft: 16 }} />
              </MenuItem>
            )}
          </MenuList>
        }
      >
        <BasicButton
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="TopNavigation__help"
          style={{
            color: theme.colors.charcoal500,
            '&:hover': {
              color: theme.colors.charcoal700,
            },
          }}
        >
          <QuestionCircleFilledIcon size={16} />
        </BasicButton>
      </Flyout>
    </>
  );
};

QuestionMarkMenu.displayName = 'QuestionMarkMenu';

QuestionMarkMenu.propTypes = {
  /** Optionally toggle menu open on initial render */
  initialOpen: PropTypes.bool,
  /** Override default links */
  helpLinks: PropTypes.shape({
    help: PropTypes.string,
    about: PropTypes.string,
  }) as Validator<HelpLinksType>,
  /** Use alternative license data (for demo purposes), this data is fetched by default */
  licenseData: licenseDataShape,
  /** current product id */
  currentProductId: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
  /** is product required */
  isProductRequired: PropTypes.bool,
};

export default QuestionMarkMenu;
